import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss, Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup, getCurrentFiscalYear } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import {
  YN_OPTIONS,
  createFundUid,
  nextSortOrderBy10,
  fundOptions,
  loadFundPackages,
  saveFundPackages,
  filterFundPackages,
  validateFundPackages,
  defaultFundPackage,
  defaultPkgMember,
  enrichPackageRows,
  loadFunds,
} from 'budget/budgetFundStore';

const PKG_GRID_ID = 'master-fund-pkg';
const MEM_GRID_ID = 'master-fund-pkg-member';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 4px;
  flex-shrink: 0;
`;

/** 그리드 행 안정 id — HiGrid getRowId가 _tempId/id만 인식 */
function withStablePkgId(row) {
  if (!row) return row;
  const uid = row.fundPkgUid || row._tempId || row.id;
  return { ...row, fundPkgUid: uid || row.fundPkgUid, id: uid || row.id, _tempId: row._tempId || uid };
}

function withStableMemberId(row) {
  if (!row) return row;
  const uid = row.memberId || row._tempId || row.id || createFundUid('PM');
  return { ...row, memberId: uid, id: uid, _tempId: row._tempId || uid };
}

function membersFromGrid(rows) {
  return (rows || []).map((m) => {
    const stable = withStableMemberId(m);
    return {
      ...stable,
      ratioBp:
        m.ratioPct != null && m.ratioPct !== ''
          ? Math.round(Number(m.ratioPct) * 100)
          : Number(m.ratioBp) || 0,
      remainderYn: m.remainderYn === 'Y' ? 'Y' : 'N',
    };
  });
}

/**
 * 기준정보 — 재원구성등록
 * 비율 패키지 마스터 + 구성원(재원·비율bp). 합계 10000(100%).
 */
const BudgetFundPackageRegisterInner = () => {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions();
  const pkgGridRef = useRef(null);
  const memGridRef = useRef(null);
  /** 화면에서 편집 중인 전체 패키지(필터 밖 포함 병합용 버퍼는 저장 시 load) */
  const [pkgCache, setPkgCache] = useState([]);
  const [selectedPkgUid, setSelectedPkgUid] = useState('');
  const [selectedHint, setSelectedHint] = useState('구성을 선택하면 구성원을 편집할 수 있습니다.');
  const [fundOpts, setFundOpts] = useState([{ value: '', label: '선택' }]);
  const pkgCacheRef = useRef([]);
  const selectedPkgUidRef = useRef('');
  /** 재원 목록 캐시 — loadFunds는 비동기이므로 조회 시 담아두고 동기 enrich에 사용 */
  const fundsRef = useRef([]);
  const year = getCurrentFiscalYear();

  useEffect(() => {
    pkgCacheRef.current = pkgCache;
  }, [pkgCache]);
  useEffect(() => {
    selectedPkgUidRef.current = selectedPkgUid;
  }, [selectedPkgUid]);

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      pkgCode: '',
      pkgName: '',
      useYn: 'Y',
    }),
    [tenantCode, year]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const getDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
    };
  }, [getValues, tenantCode, year]);

  const refreshFundOpts = useCallback(() => {
    setFundOpts(fundOptions(getDims(), { includeEmpty: true }));
  }, [getDims]);

  /** 구성원 그리드 → 선택 패키지 캐시 반영 (ref 기준 — stale state 방지) */
  const syncMembersToCache = useCallback(() => {
    const uid = selectedPkgUidRef.current;
    const cache = pkgCacheRef.current || [];
    if (!uid) return cache;
    const members = membersFromGrid(memGridRef.current?.getData?.() || []);
    return cache.map((p) => (p.fundPkgUid === uid ? { ...p, members } : p));
  }, []);

  /** 상단 구성 행의 구성원수·비율합만 갱신 (그리드 전체 setData 금지) */
  const patchPkgSummaryRow = useCallback((fundPkgUid, members) => {
    if (!fundPkgUid || !pkgGridRef.current?.setData) return;
    const enriched = enrichPackageRows([{ fundPkgUid, members }], fundsRef.current)[0];
    const cur = pkgGridRef.current.getData?.() || [];
    pkgGridRef.current.setData(
      cur.map((r) =>
        r.fundPkgUid === fundPkgUid
          ? withStablePkgId({
              ...r,
              memberCount: enriched.memberCount,
              ratioSumBp: enriched.ratioSumBp,
              ratioSumDisp: enriched.ratioSumDisp,
            })
          : r
      )
    );
  }, []);

  const showMembers = useCallback(
    (pkg, funds) => {
      if (!pkg) {
        memGridRef.current?.setData?.([]);
        setSelectedHint('구성을 선택하면 구성원을 편집할 수 있습니다.');
        return;
      }
      const enriched = enrichPackageRows([pkg], funds || fundsRef.current)[0];
      memGridRef.current?.setData?.(
        (enriched.members || []).map((m) =>
          withStableMemberId({
            ...m,
            ratioPct: m.ratioBp != null ? Number(m.ratioBp) / 100 : 0,
          })
        )
      );
      setSelectedHint(
        `${pkg.pkgCode || ''} ${pkg.pkgName || ''} · 비율합 ${enriched.ratioSumDisp || '—'}`
      );
    },
    []
  );

  /** 구성 선택: 구성원 패널만 교체. 상단 그리드 setData로 덮지 않음(추가행·mod 소실 방지) */
  const selectPkgRow = useCallback(
    (row) => {
      if (!row?.fundPkgUid) return;
      const prevUid = selectedPkgUidRef.current;
      if (prevUid && prevUid !== row.fundPkgUid) {
        const nextCache = syncMembersToCache();
        setPkgCache(nextCache);
        pkgCacheRef.current = nextCache;
        const prev = nextCache.find((p) => p.fundPkgUid === prevUid);
        if (prev) patchPkgSummaryRow(prevUid, prev.members);
      }
      setSelectedPkgUid(row.fundPkgUid);
      selectedPkgUidRef.current = row.fundPkgUid;
      const pkg =
        (pkgCacheRef.current || []).find((p) => p.fundPkgUid === row.fundPkgUid) || row;
      showMembers(pkg);
    },
    [syncMembersToCache, showMembers, patchPkgSummaryRow]
  );

  const pkgColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '구성코드', field: 'pkgCode', size: 110, required: true }),
      hiCol.edTextColumn({ header: '구성명', field: 'pkgName', size: 180, required: true }),
      hiCol.roNumberColumn({ header: '구성원수', field: 'memberCount', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '비율합', field: 'ratioSumDisp', size: 90, align: 'center' }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 80, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 80, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    []
  );

  const memColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '재원',
        field: 'fundUid',
        size: 160,
        options: fundOpts,
        required: true,
      }),
      hiCol.edNumberColumn({
        header: '비율(%)',
        field: 'ratioPct',
        size: 100,
        align: 'right',
        title: '예: 50 → 내부 5000bp',
      }),
      hiCol.edCheckboxColumn({
        header: '끝전',
        field: 'remainderYn',
        size: 72,
        title:
          '비율 안분 시 반올림 잔액(끝전)을 흡수할 재원. 1건만 지정. 지정 중에는 다른 행 체크 불가(해제 후 변경).',
        // 다른 행이 끝전(Y)이면 그 행만 편집(해제) 가능 · 나머지 비활성
        editable: (row) => {
          if (!row || row.mod === 'D') return false;
          const rows = (memGridRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
          const locked = rows.find((r) => r.remainderYn === 'Y');
          if (!locked) return true;
          const lockId = String(locked.memberId || locked.id || '');
          const rowId = String(row.memberId || row.id || '');
          return lockId === rowId;
        },
      }),
      hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 90 }),
      hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 120 }),
    ],
    [fundOpts]
  );

  const pkgGrid = useHiGridHook({
    gridRef: pkgGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: defaultFundPackage(),
  });

  const memGrid = useHiGridHook({
    gridRef: memGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: defaultPkgMember({ ratioPct: 0 }),
  });

  const handleSearch = useCallback(async () => {
    try {
      const dims = getDims();
      fundsRef.current = await loadFunds(dims);
      refreshFundOpts();
      const all = (await loadFundPackages(dims)).map(withStablePkgId);
      setPkgCache(all);
      pkgCacheRef.current = all;
      const filtered = filterFundPackages(all, getValues()).map(withStablePkgId);
      pkgGridRef.current?.setData?.(filtered);
      const curUid = selectedPkgUidRef.current;
      const still = filtered.find((r) => r.fundPkgUid === curUid);
      if (still) {
        setSelectedPkgUid(still.fundPkgUid);
        selectedPkgUidRef.current = still.fundPkgUid;
        showMembers(still);
      } else {
        setSelectedPkgUid('');
        selectedPkgUidRef.current = '';
        showMembers(null);
      }
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getDims, getValues, showMembers, refreshFundOpts]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  useEffect(() => {
    // 행 클릭·셀 포커스 모두 onSelectRow로 통일 (onRowClicked 중복 호출 방지)
    pkgGrid.onSelectRow = (row) => selectPkgRow(row);
    pkgGrid.onRowClicked = undefined;
  }, [pkgGrid, selectPkgRow]);

  // 끝전 Y 지정 시 다른 행은 N으로 정리 + 행 참조 갱신(비활성 체크 즉시 반영)
  useEffect(() => {
    memGrid.onAfterSaveCell = (row, ctx) => {
      const field = ctx?.field;
      const value = ctx?.value;
      const uid = selectedPkgUidRef.current;
      if (field === 'remainderYn' && row) {
        const mid = String(row.memberId || row.id || '');
        const data = memGridRef.current?.getData?.() || [];
        const next = data.map((r) => {
          if (r.mod === 'D') return { ...r };
          const id = String(r.memberId || r.id || '');
          if (value === 'Y') {
            if (id === mid) return { ...r, remainderYn: 'Y' };
            if (r.remainderYn === 'Y') {
              return { ...r, remainderYn: 'N', mod: r.mod === 'A' ? 'A' : 'U' };
            }
            return { ...r, remainderYn: 'N' };
          }
          // 해제: 전원 N 가능하도록 행 복사(다른 셀 editable 재평가)
          if (id === mid) return { ...r, remainderYn: 'N' };
          return { ...r };
        });
        memGridRef.current?.setData?.(next);
      }
      if (!uid) return;
      const members = membersFromGrid(memGridRef.current?.getData?.() || []);
      const nextCache = (pkgCacheRef.current || []).map((p) =>
        p.fundPkgUid === uid ? { ...p, members } : p
      );
      setPkgCache(nextCache);
      pkgCacheRef.current = nextCache;
    };
  }, [memGrid]);

  const handleAddPkg = useCallback(async () => {
    const synced = syncMembersToCache();
    const data = pkgGridRef.current?.getData?.() || [];
    const row = withStablePkgId(
      defaultFundPackage({
        fundPkgUid: createFundUid('FP'),
        sortOrder: nextSortOrderBy10(synced.length ? synced : data),
        members: [],
      })
    );
    const nextCache = [...synced, row];
    setPkgCache(nextCache);
    pkgCacheRef.current = nextCache;
    await pkgGrid.handleAdd(row);
    setSelectedPkgUid(row.fundPkgUid);
    selectedPkgUidRef.current = row.fundPkgUid;
    showMembers(row);
  }, [pkgGrid, syncMembersToCache, showMembers]);

  const handleDeletePkg = useCallback(async () => {
    const beforeUid = selectedPkgUidRef.current;
    await pkgGrid.handleDelete();
    const remaining = (pkgGridRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    const uids = new Set(remaining.map((r) => r.fundPkgUid));
    const synced = syncMembersToCache();
    const nextCache = synced.filter((p) => uids.has(p.fundPkgUid));
    setPkgCache(nextCache);
    pkgCacheRef.current = nextCache;
    if (beforeUid && !uids.has(beforeUid)) {
      setSelectedPkgUid('');
      selectedPkgUidRef.current = '';
      showMembers(null);
    }
  }, [pkgGrid, syncMembersToCache, showMembers]);

  const handleAddMember = useCallback(async () => {
    const uid = selectedPkgUidRef.current;
    if (!uid) {
      showAlert('구성을 먼저 선택하세요. (상단 그리드에서 행 선택)', 'warning');
      return;
    }
    const existing = (memGridRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    // 첫 구성원이면 끝전 기본 지정, 이미 끝전이 있으면 N
    const hasRemainder = existing.some((r) => r.remainderYn === 'Y');
    await memGrid.handleAdd(
      withStableMemberId(
        defaultPkgMember({
          ratioPct: 0,
          remainderYn: hasRemainder ? 'N' : 'Y',
        })
      )
    );
    const members = membersFromGrid(memGridRef.current?.getData?.() || []);
    const nextCache = (pkgCacheRef.current || []).map((p) =>
      p.fundPkgUid === uid ? { ...p, members } : p
    );
    setPkgCache(nextCache);
    pkgCacheRef.current = nextCache;
    patchPkgSummaryRow(uid, members);
  }, [memGrid, patchPkgSummaryRow]);

  const handleDeleteMember = useCallback(async () => {
    await memGrid.handleDelete();
    const uid = selectedPkgUidRef.current;
    if (!uid) return;
    let members = membersFromGrid(memGridRef.current?.getData?.() || []).filter(
      (m) => m.mod !== 'D'
    );
    // 끝전 행이 삭제되면 남은 첫 행에 재지정
    if (members.length && !members.some((m) => m.remainderYn === 'Y')) {
      members = members.map((m, i) => ({
        ...m,
        remainderYn: i === 0 ? 'Y' : 'N',
        mod: m.mod === 'A' ? 'A' : m.mod === 'D' ? 'D' : 'U',
      }));
      memGridRef.current?.setData?.(
        members.map((m) =>
          withStableMemberId({
            ...m,
            ratioPct: m.ratioBp != null ? Number(m.ratioBp) / 100 : 0,
          })
        )
      );
    }
    const nextCache = (pkgCacheRef.current || []).map((p) =>
      p.fundPkgUid === uid ? { ...p, members } : p
    );
    setPkgCache(nextCache);
    pkgCacheRef.current = nextCache;
    patchPkgSummaryRow(uid, members);
  }, [memGrid, patchPkgSummaryRow]);

  const handleSave = useCallback(async () => {
    try {
      const dims = getDims();
      const cache = syncMembersToCache();
      pkgCacheRef.current = cache;
      const gridPkgs = pkgGridRef.current?.getData?.() || [];
      const byUid = new Map(cache.map((p) => [p.fundPkgUid, p]));
      for (const g of gridPkgs) {
        const prev = byUid.get(g.fundPkgUid) || {};
        const memberSource =
          g.fundPkgUid === selectedPkgUidRef.current
            ? memGridRef.current?.getData?.() || prev.members
            : prev.members || g.members || [];
        byUid.set(g.fundPkgUid, {
          ...prev,
          ...g,
          members: membersFromGrid(memberSource),
        });
      }
      const activeUids = new Set(gridPkgs.filter((r) => r.mod !== 'D').map((r) => r.fundPkgUid));
      const filterVals = getValues();
      const allBefore = await loadFundPackages(dims);
      const inFilter = new Set(filterFundPackages(allBefore, filterVals).map((r) => r.fundPkgUid));
      const outside = allBefore.filter((r) => !inFilter.has(r.fundPkgUid));
      const activeInGrid = [...byUid.values()].filter((p) => activeUids.has(p.fundPkgUid));
      const merged = [...outside, ...activeInGrid];

      const v = validateFundPackages(merged);
      if (!v.ok) {
        showAlert(v.message, 'warning');
        return;
      }

      const saved = (await saveFundPackages(dims, merged)).map(withStablePkgId);
      setPkgCache(saved);
      pkgCacheRef.current = saved;
      pkgGridRef.current?.setData?.(filterFundPackages(saved, getValues()).map(withStablePkgId));
      const still = saved.find((r) => r.fundPkgUid === selectedPkgUidRef.current);
      showMembers(still || null);
      showAlert('저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [getDims, getValues, syncMembersToCache, showMembers]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'pkgCode',
          label: '구성코드',
          type: 'text',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'pkgName',
          label: '구성명',
          type: 'text',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    []
  );

  const getPkgExportData = useCallback(
    () => pkgGridRef.current?.getFilteredData?.() ?? pkgGridRef.current?.getData?.() ?? [],
    []
  );
  const getPkgExportCols = useCallback(() => pkgGridRef.current?.getDataColumns?.() ?? [], []);
  const getMemExportData = useCallback(
    () => memGridRef.current?.getFilteredData?.() ?? memGridRef.current?.getData?.() ?? [],
    []
  );
  const getMemExportCols = useCallback(() => memGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: PKG_GRID_ID,
    label: '재원구성',
    getData: getPkgExportData,
    getDataColumns: getPkgExportCols,
    joinKeys: ['fundPkgUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: MEM_GRID_ID,
    label: '구성원',
    getData: getMemExportData,
    getDataColumns: getMemExportCols,
    joinKeys: ['memberId'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '재원구성등록',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel
          ? `추가 → ${activeLabel} (F8)`
          : '추가 (F8) · 그리드 선택 Alt+F2',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel
          ? `삭제 → ${activeLabel} (F9)`
          : '삭제 (F9) · 그리드 선택 Alt+F2',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog, activeLabel]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-fund-package"
          prefsUserId={prefsUserId}
          summary="재원구성 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 순환
        {activeGridId ? ` · F8/F9 → ${activeLabel}` : ' · Alt+F2로 그리드 선택 후 F8/F9'}
        {' · '}
        비율 합계 100% 필수 · 끝전처리 재원 1건
        {' · '}
        기관 {getValues('tenantCode') || tenantCode}
      </Meta>

      <Stack>
        <Panel $flex={1.1}>
          <ActiveGridHeader gridId={PKG_GRID_ID} extra="사업·편성라인에 매핑하는 비율 패키지">
            재원구성
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={PKG_GRID_ID}
              label="재원구성"
              order={0}
              onAdd={handleAddPkg}
              onDelete={handleDeletePkg}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={PKG_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={pkgGridRef}
                  columns={pkgColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === PKG_GRID_ID}
                  {...pkgGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="재원구성"
                  gridId={PKG_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={pkgGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1}>
          <ActiveGridHeader
            gridId={MEM_GRID_ID}
            extra="재원 + 비율(%) · 합 100% · 끝전 1건"
          >
            구성원
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={MEM_GRID_ID}
              label="구성원"
              order={1}
              onAdd={handleAddMember}
              onDelete={handleDeleteMember}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={MEM_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={memGridRef}
                  columns={memColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={48}
                  showModColumn
                  headerActive={activeGridId === MEM_GRID_ID}
                  {...memGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="재원구성원"
                  gridId={MEM_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={memGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      {excelDialog}
    </PageContainer>
  );
};

const BudgetFundPackageRegister = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <BudgetFundPackageRegisterInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default BudgetFundPackageRegister;
